import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { RegistrationModel } from '@shared/extensions/user-data-extensions';
import {
  AuthorizationDeliveryMode,
  ClientCodeRequest,
  ProspectCodeRequest,
  ValidateCodeRequest
} from '@shared/webapi/MIF.Subscription.WebApi';
import { AdvertisementSourceDataModel } from '@signature/signature.extentions';
import { clientActions } from './client.actions';
import { ClientType } from './client.models';
import {
  selectAuthorizationDeliveryMode,
  selectAuthorizedClientProfile,
  selectClientAuthorizationStatus,
  selectClientAuthRequest,
  selectClientAuthResponse,
  selectClientTokenResponse,
  selectClientType,
  selectIsAuthorizingClient,
  selectIsSubmittingClientAuthRequest,
  selectPendingProspectAdvertisementSource,
  selectPendingProspectRegistration,
  selectRequestCodeClientAuthError,
  selectRequestCodeClientAuthErrorDetail,
  selectValidateCodeClientAuthError
} from './client.selectors';

@Injectable({
  providedIn: 'root'
})
export class ClientFacade {
  private readonly store = inject(Store);

  readonly clientType$ = this.store.select(selectClientType);
  readonly authRequest$ = this.store.select(selectClientAuthRequest);
  readonly pendingProspectRegistration$ = this.store.select(selectPendingProspectRegistration);
  readonly pendingProspectAdvertisementSource$ = this.store.select(selectPendingProspectAdvertisementSource);
  readonly authResponse$ = this.store.select(selectClientAuthResponse);
  readonly tokenResponse$ = this.store.select(selectClientTokenResponse);
  readonly requestCodeAuthError$ = this.store.select(selectRequestCodeClientAuthError);
  readonly requestCodeAuthErrorDetail$ = this.store.select(selectRequestCodeClientAuthErrorDetail);
  readonly validateCodeAuthError$ = this.store.select(selectValidateCodeClientAuthError);
  readonly authorizedClientProfile$ = this.store.select(selectAuthorizedClientProfile);
  readonly clientAuthorizationStatus$ = this.store.select(selectClientAuthorizationStatus);
  readonly isSubmittingClientAuthRequest$ = this.store.select(selectIsSubmittingClientAuthRequest);
  readonly isAuthorizingClient$ = this.store.select(selectIsAuthorizingClient);
  readonly authorizationDeliveryMode$ = this.store.select(selectAuthorizationDeliveryMode);

  setClientType(clientType: ClientType | null): void {
    this.store.dispatch(clientActions.setClientType({ clientType }));
  }

  restoreClientType(clientType: ClientType): void {
    this.store.dispatch(clientActions.restoreClientType({ clientType }));
  }

  setPendingProspectRegistration(registrationModel: RegistrationModel | null): void {
    this.store.dispatch(clientActions.setPendingProspectRegistration({ registrationModel }));
  }

  setPendingProspectAdvertisementSource(advertisementSourceModel: AdvertisementSourceDataModel | null): void {
    this.store.dispatch(clientActions.setPendingProspectAdvertisementSource({ advertisementSourceModel }));
  }

  clearPendingProspectData(): void {
    this.store.dispatch(clientActions.clearPendingProspectData());
  }

  cancelClientAuthentication(): void {
    this.store.dispatch(clientActions.cancelClientAuthentication());
  }

  setAuthorizationDeliveryMode(authorizationDeliveryMode: AuthorizationDeliveryMode | null): void {
    this.store.dispatch(clientActions.setAuthorizationDeliveryMode({ authorizationDeliveryMode }));
  }

  submitClientAuthentication(request: ClientCodeRequest): void {
    this.store.dispatch(clientActions.submitClientAuthentication({ request }));
  }

  submitProspectAuthentication(request: ProspectCodeRequest): void {
    this.store.dispatch(clientActions.submitProspectAuthentication({ request }));
  }

  validateClientCode(request: ValidateCodeRequest): void {
    this.store.dispatch(clientActions.validateClientCode({ request }));
  }

  authorizeValidatedClient(request: ClientCodeRequest): void {
    this.store.dispatch(clientActions.authorizeValidatedClient({ request }));
  }

  reset(): void {
    this.store.dispatch(clientActions.reset());
  }
}
